import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Delete,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../SnackbarContext";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import GlobalVariable from "../../services/Global";

const baseApiUrl = GlobalVariable.BASE_API_URL;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 650,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 4,
};

function Content_Library() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState("");
  const [rfpTypeFilter, setRfpTypeFilter] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const refreshtoken = async () => {
    const storedData = JSON.parse(localStorage.getItem("userDetails"));
    const userId = storedData.userDetails.id;
    try {
      const response = await axios.post(`${baseApiUrl}/users/refreshtoken`,
        JSON.stringify({ userid: userId }),
        { headers: { "Content-Type": "application/json" } }
      );
      if (response?.data?.status_code === 200) {
        const { userDetails, access_token } = response.data;
        localStorage.setItem(
          "userDetails",
          JSON.stringify({ userDetails, access_token })
        );
        window.location.reload();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const storedData = JSON.parse(localStorage.getItem("userDetails"));
      let accessToken = storedData?.access_token;
      
      // Set current user ID
      if (storedData?.userDetails?.id) {
        setCurrentUserId(storedData.userDetails.id);
      }

      if (searchTerm.length < 3 && searchTerm !== "") return;

      try {
        const response = await axios.get(`${baseApiUrl}/contentlibrary/contentlibrary_list?search=${searchTerm}&page=0&pageSize=10`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setLoading(true);

        // Deduplicate by (rfp_type, content_name) keeping the most recently updated
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];

        const getRowTimestamp = (row) => {
          const candidate = row?.last_updated || row?.created_date || row?.created_at || null;
          const t = candidate ? Date.parse(candidate) : NaN;
          return Number.isNaN(t) ? 0 : t;
        };

        const keyFor = (row) => {
          const type = (row?.rfp_type || '').toLowerCase().trim();
          const name = (row?.content_name || '').toLowerCase().trim();
          return `${type}|${name}`;
        };

        const latestByKey = new Map();
        for (const row of rows) {
          const key = keyFor(row);
          if (!key) continue;
          const existing = latestByKey.get(key);
          if (!existing) {
            latestByKey.set(key, row);
            continue;
          }
          const tNew = getRowTimestamp(row);
          const tOld = getRowTimestamp(existing);
          if (tNew > tOld) {
            latestByKey.set(key, row);
          } else if (tNew === tOld) {
            // Fallback: prefer higher version if timestamps are equal
            const vNew = typeof row?.version === 'number' ? row.version : -1;
            const vOld = typeof existing?.version === 'number' ? existing.version : -1;
            if (vNew > vOld) {
              latestByKey.set(key, row);
            }
          }
        }

        // Optionally sort by last_updated desc for consistent display
        const deduped = Array.from(latestByKey.values()).sort((a, b) => getRowTimestamp(b) - getRowTimestamp(a));
        setData(deduped);
        setCurrentPage(1); // Reset to first page when search changes
      } catch (error) {
        if (error.response && error.response.status === 401) {
          refreshtoken();
        } else {
          console.error("Error fetching data:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchTerm]);

  // Filter data based on RFP Type selection
  useEffect(() => {
    if (!data || data.length === 0) {
      setFilteredData([]);
      return;
    }
    
    if (rfpTypeFilter === null || rfpTypeFilter === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) => {
        const itemType = (item.rfp_type || "").toLowerCase().trim();
        return itemType === rfpTypeFilter.toLowerCase();
      });
      setFilteredData(filtered);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [rfpTypeFilter, data]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleRfpTypeFilterChange = (event) => {
    setRfpTypeFilter(event.target.value);
  };

  const handleRowClick = (row) => {
    navigate(`/section-details/${row.id}`, { state: { row } });
  };

  const truncateAndRemoveTags = (text, maxLength) => {
    if (!text) return "";
    let strippedText = text.replace(/<[^>]+>/g, "").trim();
    strippedText = strippedText.replace(/&nbsp;/g, " ");
  
    return strippedText.length <= maxLength
      ? strippedText
      : `${strippedText.substring(0, maxLength)}...`;
  };

  const handleDeleteIconClick = (prompt) => {
    setContentToDelete(prompt);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setContentToDelete(null);
  };

  const handleDeleteContent = async (id) => {
    const storedData = JSON.parse(localStorage.getItem("userDetails"));
    const accessToken = storedData.access_token;

    try {
      const response = await axios.delete(`${baseApiUrl}/contentlibrary/delclsection`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: { id },
      });

      if (response.data.status_code === 200) {
        const newData = data.filter((prompt) => prompt.id !== id);
        setData(newData);
        
        // If current page is empty after deletion, go to previous page
        const totalPages = Math.ceil(newData.length / rowsPerPage);
        if (currentPage > totalPages && totalPages > 0) {
          setCurrentPage(totalPages);
        }
        
        setDeleteConfirmOpen(false);
        showSnackbar("Content deleted successfully.");
      } else {
        console.error("Failed to delete prompt:", response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        console.error(
          "Error:",
          error.response.data.msg || error.response.data.message
        );
      } else {
        console.error("Error deleting prompt:", error);
      }
    }
  };

  // Pagination calculations - use filteredData if filter is applied, otherwise use data
  const displayData = rfpTypeFilter !== null && rfpTypeFilter !== "" ? filteredData : data;
  const totalPages = Math.ceil(displayData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, displayData.length);
  const paginatedData = displayData.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <Container
      fluid
      className="rfpDtailsContainer"
      maxWidth="100%"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          flexGrow: 1,
          mt: 2,
          boxShadow: 10,
          borderRadius: "10px",
          padding: "20px",
          height: "100%"
        }}
      >
        <Box
          className="search-and-add-section"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            mb: 2,
          }}
        >
          <Typography
            className="MuiTypography-h5"
            variant="h5"
            sx={{ fontWeight: 600 }}
          >
            Content Library
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              mt: { xs: 2, md: 0 },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <TextField
              className="MuiTextField-root search-bar"
              placeholder="Search by RFP Name"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              sx={{
                fontSize: { xs: "12px", md: "12px" },
                mb: { xs: 1, sm: 0 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "9px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ opacity: "40%" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl 
              size="small" 
              className='inputfields'
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                mb: { xs: 1, sm: 0 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "9px",
                },
              }}
            >
              <InputLabel sx={{ fontSize: '12px' }} className='fontstyleet' id="rfp-type-select-label">RFP Type</InputLabel>
              <Select
                labelId="rfp-type-select-label"
                className='inputfields rounded-3'
                value={rfpTypeFilter === null ? "" : rfpTypeFilter}
                onChange={handleRfpTypeFilterChange}
                label="RFP Type"
                MenuProps={{
                  PaperProps: {
                    style: {
                      marginTop: '6px',
                      borderRadius: '10px',
                    },
                  },
                }}
                IconComponent={rfpTypeFilter && rfpTypeFilter !== "" ? () => null : undefined}
                endAdornment={rfpTypeFilter && rfpTypeFilter !== "" && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRfpTypeFilter(null)}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                )}
              >
                <MenuItem value="solutioning">Solutioning</MenuItem>
                <MenuItem value="staffing">Staffing</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={() => navigate("/add-new-section")}
              className="Buttons_ESD3 MuiButton-AddNew bg-primary text-capitalize rounded-3"
              sx={{
                bgcolor: "#429bf5",
                mt: { xs: 0, sm: 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Add New Section
            </Button>
          </Box>
        </Box>
        <TableContainer
          className="table-container"
          component={Paper}
          sx={{
            width: "100%",
            boxShadow: 2,
            padding: 2,
            borderRadius: "15px",
            flexGrow: 1,
          }}
        >

          <Table>
            <TableHead className="TableData">
              <TableRow>
                <TableCell className="fontstyleeb">S.No</TableCell>
                <TableCell
                  className="cell-border-right fontstyleeb"
                >
                  Sections Name
                </TableCell>
                <TableCell className="fontstyleeb">RFP Type</TableCell>
                <TableCell className="fontstyleeb">Description</TableCell>
                <TableCell className="fontstyleeb">Last Updated</TableCell>
                {/* <TableCell className="fontstyleeb">Delete</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <Box
                  className="circular-progress"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                  }}
                >
                  <CircularProgress className=" text-primary" />
                </Box>
              )}
              {paginatedData.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ fontSize: { xs: "12px", md: "14px" } }}>
                    {startIndex + index + 1}
                  </TableCell>
                  <TableCell
                    className="fontstylee text-primary cell-border-right  "
                    sx={{
                      fontSize: { xs: "12px", md: "13px", lg: "14px" },
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => handleRowClick(row)}
                  >
                    {row.content_name}
                  </TableCell>
                  <TableCell
                    sx={{ 
                      fontSize: { xs: "10px", md: "11px", lg: "12px" },
                      textTransform: 'capitalize'
                    }}
                  >
                    {row.rfp_type || 'N/A'}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: "10px", md: "11px", lg: "12px" } }}
                  >
                    {truncateAndRemoveTags(row.content_description, 50)}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: "10px", md: "11px", lg: "12px" } }}
                  >
                    {row.last_updated ? (() => {
                      const dateObj = new Date(row.last_updated);
                      const options = { timeZone: "Asia/Kolkata" };
                      const ist = new Date(dateObj.toLocaleString("en-US", options));
                      const formatted = ist.toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                      }).replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
                      return formatted.replace(",", "") + " UTC";
                    })() : 'N/A'}
                  </TableCell>
                  {/* <TableCell>
                    {currentUserId && row.user_id === currentUserId ? (
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteIconClick(row)}
                        >
                          <Delete
                            className="delete-icon"
                          />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary" style={{ marginLeft: "20px" }}>
                        -
                      </Typography>
                    )}
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {displayData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              px: 2,
            }}
          >
            <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: "text.secondary" }}>
              Rows {startIndex + 1}-{endIndex} of {displayData.length}
            </Typography>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: "text.secondary" }}>
                Page {currentPage} of {totalPages || 1}
              </Typography>
              
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  sx={{
                    color: currentPage === 1 ? "disabled" : "primary",
                    "&:hover": {
                      bgcolor: currentPage === 1 ? "transparent" : "action.hover",
                    },
                  }}
                >
                  <ArrowBack />
                </IconButton>
                
                <IconButton
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  sx={{
                    color: currentPage === totalPages || totalPages === 0 ? "disabled" : "primary",
                    "&:hover": {
                      bgcolor: currentPage === totalPages || totalPages === 0 ? "transparent" : "action.hover",
                    },
                  }}
                >
                  <ArrowForward />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        <Modal
          open={deleteConfirmOpen}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={{ ...style, width: "500px" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Confirm Delete
              </Typography>
              <CloseIcon
                onClick={handleCloseDeleteDialog}
                style={{ cursor: "pointer" }}
              />
            </Box>
            <Divider style={{ borderColor: "#adadad" }} />
            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
              Are you sure you want to delete?
            </Typography>
            <Box mt={4} display="flex" justifyContent="end">
              <Button
                variant="outlined"
                className="border-primary text-primary rounded-3"
                onClick={handleCloseDeleteDialog}
              >
                No
              </Button>
              <Button
                variant="contained"
                className="bg-primary mx-2 rounded-3"
                onClick={() => handleDeleteContent(contentToDelete.id)}
              >
                Yes
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Container>
  );
}

export default Content_Library;